import * as AccordionPrimitive from '@radix-ui/react-accordion';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import { cn } from '@plato/ui/util/classes';
import { ChevronDownIcon } from 'lucide-react';

const QuoteOrderTimelineAccordion = AccordionPrimitive.Root;

const QuoteOrderTimelineAccordionItem = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
    <AccordionPrimitive.Item ref={ref} className={cn('', className)} {...props} />
));
QuoteOrderTimelineAccordionItem.displayName = 'QuoteOrderTimelineAccordionItem';

type QuoteOrderTimelineAccordionTriggerProps = {
    enableTrigger?: boolean;
};

const QuoteOrderTimelineAccordionTrigger = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> &
        QuoteOrderTimelineAccordionTriggerProps
>(({ className, children, enableTrigger = true, ...props }, ref) => {
    const { t } = useTranslation();
    return (
        <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
                disabled={!enableTrigger}
                ref={ref}
                className={cn(
                    'flex w-full flex-col text-left transition-all [&[data-state=open]>div>#close-text]:hidden [&[data-state=open]>div>#open-text]:flex [&[data-state=open]>div>svg]:rotate-180',
                    className,
                )}
                {...props}
            >
                {children}
                {enableTrigger ? (
                    <div className="flex w-full items-center sm:justify-end py-3 text-blue-600 gap-2">
                        <LinkText id="close-text" className="flex">
                            {t('show_more')}
                        </LinkText>
                        <LinkText className="hidden" id="open-text">
                            {t('show_less')}
                        </LinkText>
                        <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform duration-200" />
                    </div>
                ) : (
                    <div className="h-3" />
                )}
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    );
});
QuoteOrderTimelineAccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const QuoteOrderTimelineAccordionContent = React.forwardRef<
    React.ElementRef<typeof AccordionPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
    <AccordionPrimitive.Content
        ref={ref}
        className={cn(
            'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm',
            className,
        )}
        {...props}
    >
        {children}
    </AccordionPrimitive.Content>
));
QuoteOrderTimelineAccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {
    QuoteOrderTimelineAccordion,
    QuoteOrderTimelineAccordionContent,
    QuoteOrderTimelineAccordionItem,
    QuoteOrderTimelineAccordionTrigger,
};
